import { USD, CAD, EUR, GBP, BRL, MXN } from 'dinero.js'
import type { DineroCurrency } from 'dinero.js'

/**
 * ISO code -> dinero currency object.
 * Extend this map when new currencies are added to the DB.
 */
export const CURRENCY_MAP: Record<string, DineroCurrency<number>> = {
  USD,
  CAD,
  EUR,
  GBP,
  BRL,
  MXN,
}

/**
 * @deprecated Legacy numeric ID map. Use currency_code from the API instead.
 * API numeric ID -> ISO 4217 code.
 * Matches the `currencies` table in the backend DB.
 */
export const CURRENCY_ID_MAP: Record<number, string> = {
  1: 'CAD',
  2: 'USD',
}

/**
 * Default BCP 47 locale for each currency.
 * Used when no explicit locale is provided.
 */
export const DEFAULT_LOCALE: Record<string, string> = {
  USD: 'en-US',
  CAD: 'en-CA',
  EUR: 'de-DE',
  GBP: 'en-GB',
  BRL: 'pt-BR',
  MXN: 'es-MX',
}

/**
 * Normalize a numeric API ID or ISO code string into an ISO 4217 code.
 *
 * @param idOrCode - Numeric currency ID (e.g. 1) or ISO string (e.g. 'CAD')
 * @returns Uppercase ISO 4217 code
 * @throws When the input cannot be resolved to a known currency
 */
export function resolveCurrencyCode(idOrCode: number | string): string {
  if (typeof idOrCode === 'number') {
    const code = CURRENCY_ID_MAP[idOrCode]
    if (!code) {
      throw new Error(`Unknown currency ID: ${idOrCode}`)
    }
    return code
  }

  const upper = idOrCode.toUpperCase()
  if (!CURRENCY_MAP[upper]) {
    throw new Error(`Unknown currency code: ${idOrCode}`)
  }
  return upper
}

/**
 * Get the dinero currency object for a given ISO code.
 * Falls back to CAD if the code is not in the map.
 */
export function getDineroCurrency(code: string): DineroCurrency<number> {
  return CURRENCY_MAP[code.toUpperCase()] ?? CAD
}
